import { useEffect, useState } from 'react'

import { Button, toast } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { reportsTimeApi } from '@/api/reportsTime.api'
import { PageHeader } from '@/components/ui/PageHeader'
import useUnsavedChangesGuard from '@/hooks/useUnsavedChangesGuard'
import { useTimesheetStore } from '@/store/timesheet.store'

import { CalendarSidebar } from '../components/CalendarSidebar'
import { DayActivitiesCard } from '../components/DayActivitiesCard'
import { DayDetailsCard } from '../components/DayDetailsCard'

export default function TimesheetForm() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  const { id } = useParams<{ id: string }>()
  const filters = useTimesheetStore(s => s.filters)
  const fetchData = useTimesheetStore(s => s.fetchData)
  const report = useTimesheetStore(s => s.report)
  const [isInitializing, setIsInitializing] = useState(!!id)
  const [isSaving, setIsSaving] = useState(false)
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [originalItems, setOriginalItems] = useState<string | null>(null)

  useEffect(() => {
    async function init() {
      if (id) {
        try {
          const report = await reportsTimeApi.getTimeReport(id)
          useTimesheetStore.setState({
            reportId: id,
            report,
            filters: {
              driverID: report.Driver?.ID,
              year: report.Year,
              month: report.Month,
            },
            selectedDay: null,
          })
          setOriginalItems(JSON.stringify(report.Items || []))
        } catch (error) {
          console.error('Failed to fetch time report for editing', error)
        }
      } else {
        useTimesheetStore.setState({
          reportId: undefined,
          report: undefined,
          filters: {
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            driverID: undefined,
          },
          selectedDay: null,
        })
        setOriginalItems(JSON.stringify([]))
      }
      setIsInitializing(false)
      fetchData()
    }
    init()
  }, [id, fetchData])

  const currentItemsStr = JSON.stringify(report?.Items || [])
  const isDirty = originalItems !== null && originalItems !== currentItemsStr
  const { dialog: unsavedDialog, guardRef, requestBack } = useUnsavedChangesGuard(isDirty)

  if (isInitializing) {
    return <div className="p-5">Загрузка...</div>
  }

  const handleSave = async () => {
    const items = report?.Items || []
    const hasEmptyActivity = items.some(item => !item.Activity?.trim())
    const hasEmptyDate = items.some(item => !item.StartDate || !item.EndDate)
    if (hasEmptyActivity || hasEmptyDate) {
      setShowValidationErrors(true)
      toast.danger(t('timesheet.validation_error', 'Пожалуйста, заполните все периоды и активности'))
      return
    }

    setIsSaving(true)
    try {
      if (id && report) {
        await reportsTimeApi.updateTimeReport(id, {
          DriverID: report.Driver?.ID || filters.driverID!,
          Year: report.Year,
          Month: report.Month,
          Items: report.Items || []
        })
        guardRef.current = false
        toast.success(t('timesheet.toast.update_success'))
      } else if (!id && filters.driverID) {
        await reportsTimeApi.createTimeReport({
          DriverID: filters.driverID,
          Year: filters.year,
          Month: filters.month,
          Items: report?.Items || []
        })
        guardRef.current = false
        toast.success(t('timesheet.toast.create_success'))
      }
      navigate(id ? `/timesheet/${id}` : '/timesheet')
    } catch (err: any) {
      console.error(err)
      toast.danger(id ? t('timesheet.toast.update_error') : t('timesheet.toast.create_error'), { description: err.message })
    } finally {
      setIsSaving(false)
    }
  }

  const driverName = report?.Driver?.Name || ''
  const formattedMonthYear = report
    ? new Date(report.Year, report.Month - 1).toLocaleString(i18n.resolvedLanguage === 'ru' ? 'ru-RU' : 'en-US', { month: 'long', year: 'numeric' })
    : ''

  const pageTitle = id
    ? t('timesheet.edit_title', { driverName, period: formattedMonthYear })
    : t('timesheet.create_title')

  return (
    <div className="p-5 h-full flex flex-col space-y-5">
      {unsavedDialog}
      <div className="flex justify-between items-start">
        <PageHeader
          onBack={requestBack}
          title={pageTitle}
          subtitle={t('timesheet.subtitle')}
        />
      </div>

      <div className="flex-1 flex gap-5 overflow-hidden min-h-0">
        <CalendarSidebar />

        <div className="flex-1 flex flex-row gap-5 min-h-0">
          <div className="flex-1 min-h-0 max-h-[75vh]">
            <DayDetailsCard />
          </div>
          <div className="flex-2 min-h-0 max-h-[75vh]">
            <DayActivitiesCard showErrors={showValidationErrors} />
          </div>
        </div>
      </div>

      <div className="px-5 py-4 flex items-center justify-end border-t border-zinc-100 dark:border-zinc-800">
        <div className="flex gap-2">
          <Button
            variant="ghost"
            isDisabled={isSaving}
            onClick={requestBack}
          >
            {t('common.cancel')}
          </Button>
          <Button
            isDisabled={isSaving || (!id && !filters.driverID)}
            onPress={handleSave}
          >
            {isSaving ? '...' : (id ? t('common.save') : t('common.create'))}
          </Button>
        </div>
      </div>
    </div>
  )
}
